import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Client } from '@/api/clients/clientsApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { PhoneInput } from '@/components/ui/phoneInput';
import { capitalizeWordsAsYouType } from '@/utils/inputFormatting';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';

interface EditDetailsDialogProps {
  profile: Client;
  isSaving: boolean;
  onSave: (phone: string | null, city: string | null) => void;
  onCancel: () => void;
}

function toNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function EditDetailsDialog({ profile, isSaving, onSave, onCancel }: EditDetailsDialogProps) {
  const { t } = useTranslation(['clients', 'common']);
  const [phone, setPhone] = useState(profile.phone === null ? '' : profile.phone);
  const [city, setCity] = useState(profile.city === null ? '' : profile.city);
  const [isPhoneValidationVisible, setIsPhoneValidationVisible] = useState(false);

  return (
    <ModalOverlay onRequestClose={onCancel} variant="sheet">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-zoowhite border border-zooborder rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:pb-6">
        <h2 className="text-lg font-bold text-zooindigo">{t('clients:yourDetails.editTitle')}</h2>
        <p className="text-sm text-zoomuted mt-1.5">{t('clients:yourDetails.editHint')}</p>
        <div className="space-y-4 mt-4">
          <div className="space-y-1.5">
            <Label htmlFor="own-details-phone">{t('clients:form.phone')}</Label>
            <PhoneInput
              inputId="own-details-phone"
              value={phone}
              isValidationVisible={isPhoneValidationVisible}
              onChange={setPhone}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="own-details-city">{t('clients:form.city')}</Label>
            <Input
              id="own-details-city"
              value={city}
              onChange={function update(changeEvent) {
                setCity(capitalizeWordsAsYouType(changeEvent.target.value));
              }}
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSaving}
            onClick={onCancel}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            className="flex-1"
            isLoading={isSaving}
            onClick={function save() {
              if (isBlankOrValidPhoneNumber(phone) === false) {
                setIsPhoneValidationVisible(true);
                return;
              }
              onSave(toNullable(phone), toNullable(city));
            }}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
